import { customBaseUrl, httpGet } from '../store/http';

// ────────────────────────────────────────────────────────────────────────────
// Activity Log Service
// Base URL: customBaseUrl.logUrl  (update if backend uses a different service)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a query string from params, ignoring null/undefined/empty values.
 */
const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

/**
 * Normalizes individual activity log items to ensure fields conform to the expected format.
 */
const normalizeActivityLogItem = (log, index) => {
  if (!log) return {};
  return {
    ...log,
    id: log.id || log._id || log.logId || `activity-log-${index}`,
    userName: log.userName || log.adminName || (log.adminEmail && log.adminEmail.trim()) || log.userId || log.user || log.fullName || log.username || log.email || '—',
    userType: log.userType || 'ADMIN',
    action: log.action || log.description || log.activity || log.eventType?.replace(/_/g, ' ') || '—',
    module: log.module || log.entityType || log.targetEntityType || log.moduleName || log.component || log.service || '—',
    timestamp: log.timestamp || log.duration || log.createdAt || log.time || log.date || log.eventTime || log?.approvedAt,
    ipAddress: log.ipAddress || log.ip || log.clientIp || '',
    deviceType: log.deviceType || log.device || log.userAgent || '',
    userId: log.userId || log.adminUserId || log.adminId || log.id || '',
    beforeValue: log.beforeValue || log.oldValues || '',
    afterValue: log.afterValue || log.newValues || '',
    loginStatus: log.loginStatus || log.status || '',
    channel: log.channel || log.userAgent || '',
  };
};

/**
 * GET /audit/admin/events/all OR GET /audit/events/search
 * Fetch all user activity logs via the audit-reporting endpoints.
 *
 * Query params: page, size, sortBy, sortDir, etc.
 */
export const getAllActivityLogs = async (params = {}) => {
  const {
    page,
    size,
    pageNo,
    pageSize,
    sortBy = 'timestamp',
    sortDir = 'desc',
    startDate,
    startTime,
    endDate,
    endTime,
    ...rest
  } = params;

  // page: 0-indexed page number. pageNo: 1-indexed page number.
  const pageNumber = page !== undefined ? page : (pageNo !== undefined ? pageNo - 1 : 0);
  const sizeNumber = size !== undefined ? size : (pageSize !== undefined ? pageSize : 20);

  const hasFilters = !!(
    rest.userId ||
    rest.search ||
    rest.eventType ||
    rest.actionType ||
    rest.entityType ||
    rest.module ||
    rest.status ||
    rest.loginStatus ||
    startDate ||
    startTime ||
    endDate ||
    endTime
  );

  const queryParams = {
    page: pageNumber,
    size: sizeNumber,
    sortBy,
    sortDir,
  };

  let endpoint = '/audit/admin/events/all';

  if (hasFilters) {
    endpoint = '/audit/events/search';
    queryParams.userId = rest.userId || rest.search || undefined;
    queryParams.eventType = rest.eventType || (rest.actionType ? rest.actionType.toUpperCase().replace(/\s+/g, '_') : undefined);
    queryParams.entityType = rest.entityType || (rest.module ? rest.module.toUpperCase().replace(/\s+/g, '_') : undefined);
    queryParams.status = rest.status || rest.loginStatus || undefined;
    queryParams.startTime = startTime || startDate || undefined;
    queryParams.endTime = endTime || endDate || undefined;
  } else {
    // If not filtering, forward any other custom parameter that might be sent
    Object.assign(queryParams, rest);
  }

  const query = buildQuery(queryParams);
  const res = await httpGet(`${customBaseUrl.auditUrl}${endpoint}${query}`);

  if (res && (res.success === true || res.status === true || res.respCode === '00' || res.respCode === '200' || res.respCode === 0 || res.respCode === 200)) {
    const dataBody = res.data || res.respBody || {};
    const content = dataBody.content || (Array.isArray(dataBody) ? dataBody : []);
    return {
      status: true,
      data: {
        content: Array.isArray(content) ? content.map(normalizeActivityLogItem) : [],
        totalElements: dataBody.totalElements || dataBody.total || (Array.isArray(dataBody) ? dataBody.length : 0),
      },
    };
  }

  return {
    status: false,
    message: res?.message || res?.respDesc || 'Failed to fetch activity logs',
    data: {
      content: [],
      totalElements: 0,
    },
  };
};

/**
 * GET /audit-reporting/admin/activity/reports
 * Fetch admin activity reports.
 *
 * Query params: pageNo, pageSize, search, module, actionType, startDate, endDate
 */
export const getAdminActivityLogs = async (params = {}) => {
  const { page, size, pageNo, pageSize, ...rest } = params;
  const pageNumber = pageNo !== undefined ? pageNo : (page !== undefined ? page : 1);
  const sizeNumber = pageSize !== undefined ? pageSize : (size !== undefined ? size : 10);

  const queryParams = {
    pageNo: pageNumber,
    pageSize: sizeNumber,
    ...rest,
  };

  const filterKeys = Object.keys(rest).filter((key) => rest[key] != null && rest[key] !== '');
  const endpoint = filterKeys.length > 0 ? '/admin/activity/reports/filter' : '/admin/activity/reports';

  const query = buildQuery(queryParams);
  return await httpGet(`${customBaseUrl.auditUrl}${endpoint}${query}`);
};

/**
 * GET /api/v1/admin/activity-logs?userType=CUSTOMER
 * Fetch customer-only activity logs.
 */
export const getCustomerActivityLogs = async (params = {}) => {
  return getAllActivityLogs({ ...params, userType: 'CUSTOMER' });
};

/**
 * GET /api/v1/admin/activity-logs?actionType=LOGIN
 * Fetch login activity logs (login attempts only).
 */
export const getLoginActivityLogs = async (params = {}) => {
  return getAllActivityLogs({ ...params, actionType: 'LOGIN' });
};

/**
 * GET /api/v1/admin/activity-logs/:logId
 * Fetch a single activity log entry by its ID.
 */
export const getActivityLogDetail = async (logId) => {
  return await httpGet(`${customBaseUrl.logUrl}/api/v1/admin/activity-logs/${logId}`);
};

/**
 * GET /api/v1/admin/activity-logs/export
 * Fetch all filtered logs without pagination (for CSV/Excel export).
 *
 * Query params: search, module, userType, actionType, startDate, endDate
 */
export const exportActivityLogs = async (params = {}) => {
  const { page, size, ...rest } = params; // strip pagination params
  const query = buildQuery(rest);
  return await httpGet(`${customBaseUrl.logUrl}/api/v1/admin/activity-logs/export${query}`);
};

/**
 * GET /api/v1/admin/activity-logs/stats
 * Fetch summary counts for the stats cards.
 */
export const getActivityLogStats = async () => {
  return await httpGet(`${customBaseUrl.logUrl}/api/v1/admin/activity-logs/stats`);
};
